import { FolderOpen, ListChecks, Play, RefreshCw, Square } from 'lucide-react'

import { Button } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import type { TestingAccess, TextRunsActions, TextRunsVm } from '../model/types'
import { Share, StateBadge, TestingGate } from './TestingParts'

/** Поиск по тексту · Замеры (RP-FB34): последний балл, история и запуск нового замера по всем запросам. */
export function TextRunsScreen({ access, vm, actions }: { access: TestingAccess; vm: TextRunsVm; actions: TextRunsActions }) {
  return (
    <TestingGate access={access}>
      <section aria-labelledby="platform-text-runs-title" className="platform-testing">
        <header className="platform-resources__toolbar">
          <h2 id="platform-text-runs-title">Поиск по тексту · Замеры</h2>
          <div className="platform-testing__row">
            <Button
              variant="ghost"
              icon={<ListChecks size={16} aria-hidden="true" />}
              onClick={actions.openQueries}
              actionId="platform.testing-text-runs.open-queries"
            >
              Запросы
            </Button>
            <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.testing-text-runs.reload">
              Обновить
            </Button>
          </div>
        </header>
        <p className="platform-testing__note">Каталог замера — тестовый: баллы сравнивают замеры между собой, а не обещают такую же точность в магазине.</p>
        <ActionError error={vm.action.error} onReload={actions.reload} />

        {vm.latest !== null && (
          <section aria-label="Последний замер">
            <h3>{vm.latest.title}</h3>
            <dl className="platform-testing__scores">
              {vm.latest.scores.map((score) => (
                <div key={score.label}>
                  <dt>{score.label}</dt>
                  <dd>
                    <Share share={score.share} />
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <section aria-label="Новый замер">
          <p className="platform-testing__note">Каждый запрос пройдёт тот же путь, что поиск в номенклатуре; замер идёт в фоне, экран можно закрыть.</p>
          <div className="platform-testing__actions">
            <Button
              variant="primary"
              icon={<Play size={16} aria-hidden="true" />}
              disabled={vm.running !== null || vm.action.busy}
              disabledReason={vm.running}
              onClick={actions.start}
              actionId="platform.testing-text-runs.start"
            >
              Запустить замер
            </Button>
          </div>
        </section>

        <section aria-label="Замеры">
          <h3>Замеры</h3>
          {vm.query.status !== 'ready' ? (
            <QueryState query={vm.query} emptyText="Замеров ещё не было" onRetry={actions.reload} />
          ) : (
            <div className="platform-sync__table-wrap">
              <table className="platform-sync__table">
                <thead>
                  <tr>
                    <th>Когда</th>
                    <th>Состояние</th>
                    <th>Первое место</th>
                    <th>В пятёрке</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {vm.rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.when}</td>
                      <td>
                        <StateBadge badge={row.state} />
                        <div className="platform-testing__note">{row.progress}</div>
                      </td>
                      <td>{row.top1 === null ? '—' : <Share share={row.top1} />}</td>
                      <td>{row.top5 === null ? '—' : <Share share={row.top5} />}</td>
                      <td>
                        <div className="platform-testing__row">
                          <Button
                            variant="ghost"
                            icon={<FolderOpen size={16} aria-hidden="true" />}
                            aria-label={`Открыть замер ${row.when}`}
                            onClick={() => actions.open(row.id)}
                            actionId="platform.testing-text-runs.open"
                          >
                            Открыть
                          </Button>
                          {row.cancellable && (
                            <Button
                              variant="ghost"
                              icon={<Square size={16} aria-hidden="true" />}
                              aria-label={`Отменить замер ${row.when}`}
                              onClick={() => actions.cancel(row.id)}
                              actionId="platform.testing-text-runs.cancel"
                            >
                              Отменить
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </section>
    </TestingGate>
  )
}
